import { useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { isUnauthorizedError } from "@/lib/authUtils";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { CommandPalette } from "@/components/command-palette";
import type { CarrierWithRelations, Company, User } from "@shared/schema";
import { track } from "@/lib/analytics";
import { 
  Plus, 
  ChevronDown, 
  FileText, 
  Building2, 
  Truck, 
  Users, 
  MessageSquare,
  ClipboardList
} from "lucide-react";

// Import modal components
import CreateWorkOrderModal from "@/components/modals/create-work-order-modal";
import CreateCompanyModal from "@/components/modals/create-company-modal";
import CreateCarrierModal from "@/components/modals/create-carrier-modal";
import CreateContactModal from "@/components/modals/create-contact-modal";
import FMCSACarriersBulkModal from "@/components/modals/fmcsa-carriers-bulk-modal";

interface NewButtonProps {
  currentPage?: string;
}

export default function NewButton({ currentPage = "dashboard" }: NewButtonProps) {
  const { toast } = useToast();
  const { user: currentUser } = useAuth();
  const [, setLocation] = useLocation();
  const [activeModal, setActiveModal] = useState<string | null>(null);
  const [showFMCSABulkModal, setShowFMCSABulkModal] = useState(false);
  const [newlyCreatedCompany, setNewlyCreatedCompany] = useState<{
    id: number;
    name: string;
    dotNumber?: string | null;
    dotProfile?: { dotNumber?: string | null } | null;
  } | null>(null);
  const [prefillCompany, setPrefillCompany] = useState<{ id: number; name: string } | null>(null);

  // Fetch data for modals
  const { data: carriers } = useQuery<CarrierWithRelations[]>({
    queryKey: ["/api/carriers"],
    retry: false,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const { data: companies } = useQuery<Company[]>({
    queryKey: ["/api/companies", { context: "select" }],
    queryFn: async () => {
      const res = await fetch("/api/companies?limit=200", { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch companies");
      return res.json();
    },
    retry: false,
    select: (d: any) => d?.data ?? (Array.isArray(d) ? d : []),
  });

  const { data: users } = useQuery<User[]>({
    queryKey: ["/api/assignable-users"],
    retry: false,
  });

  // Work Order mutation
  const createWorkOrderMutation = useMutation({
    mutationFn: async (workOrderData: any) => {
      const response = await apiRequest("POST", "/api/work-orders", workOrderData);
      return await response.json();
    },
    onSuccess: (createdWorkOrder) => {
      track("work_order_created", {
        source_page: "new_button",
        work_order_id: createdWorkOrder?.id ?? null,
        company_id: createdWorkOrder?.companyId ?? null,
        carrier_id: createdWorkOrder?.carrierId ?? null,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/work-orders/list"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      toast({
        title: "Success",
        description: "Work order created successfully",
      });
      setActiveModal(null);
      if (createdWorkOrder?.id) {
        setLocation(`/work-orders/${createdWorkOrder.id}`);
      }
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to create work order",
        variant: "destructive",
      });
    },
  });

  // Company mutation
  const createCompanyMutation = useMutation({
    mutationFn: async ({ companyData, officersToCreate, hasDotData }: { 
      companyData: any; 
      officersToCreate?: Array<{ firstName: string; lastName: string; email: string | null }>; 
      hasDotData?: boolean 
    }) => {
      const response = await apiRequest("POST", "/api/companies", companyData);
      const newCompany = await response.json();
      
      if (officersToCreate && officersToCreate.length > 0) {
        for (let i = 0; i < officersToCreate.length; i++) {
          const officer = officersToCreate[i];
          const contactData = {
            firstName: officer.firstName,
            lastName: officer.lastName,
            email: officer.email || undefined,
          };
          
          const contactResponse = await apiRequest("POST", "/api/contacts", contactData);
          const newContact = await contactResponse.json();
          
          // First officer is set as primary contact, all officers get "owner" role
          await apiRequest("POST", `/api/contacts/${newContact.id}/companies`, {
            companyId: newCompany.id,
            relationshipType: "owner",
            isPrimary: i === 0,
          });
        }
      }
      
      return { ...newCompany, hasDotData, type: companyData.type };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      const officerCount = variables.officersToCreate?.length || 0;
      toast({
        title: "Success",
        description: officerCount > 0 
          ? `Company created successfully with ${officerCount} officer contact(s)`
          : "Company created successfully",
      });
      setActiveModal(null);
      
      // If this is a direct client (not service bureau) with DOT data, 
      // offer to fetch carriers from FMCSA
      if (result.hasDotData && result.type !== "service_bureau" && (result.dotProfile?.dotNumber || result.dotNumber)) {
        setNewlyCreatedCompany(result);
        setShowFMCSABulkModal(true);
      } else {
        setPrefillCompany({ id: result.id, name: result.name });
        setActiveModal("contact");
      }
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to create company",
        variant: "destructive",
      });
    },
  });

  // Carrier mutation
  const createCarrierMutation = useMutation({
    mutationFn: async (carrierData: any) => {
      await apiRequest("POST", "/api/carriers", carrierData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      toast({
        title: "Success",
        description: "Carrier created successfully",
      });
      setActiveModal(null);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to create carrier",
        variant: "destructive",
      });
    },
  });

  // Contact mutation
  const createContactMutation = useMutation({
    mutationFn: async (contactData: any) => {
      await apiRequest("POST", "/api/contacts", contactData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/contacts"] });
      toast({
        title: "Success",
        description: "Contact created successfully",
      });
      setActiveModal(null);
    },
    onError: (error) => {
      if (isUnauthorizedError(error)) {
        toast({
          title: "Unauthorized",
          description: "You are logged out. Logging in again...",
          variant: "destructive",
        });
        setTimeout(() => {
          window.location.href = "/api/login";
        }, 500);
        return;
      }
      toast({
        title: "Error",
        description: "Failed to create contact",
        variant: "destructive",
      });
    },
  });

  const handleWorkOrderSubmit = (workOrderData: any) => {
    createWorkOrderMutation.mutate(workOrderData);
  };

  const handleCompanySubmit = (
    companyData: any, 
    officersToCreate?: Array<{ firstName: string; lastName: string; email: string | null }>, 
    hasDotData?: boolean
  ) => {
    createCompanyMutation.mutate({ companyData, officersToCreate, hasDotData });
  };

  const handleCarrierSubmit = (carrierData: any) => {
    createCarrierMutation.mutate(carrierData);
  };

  const handleContactModalClose = () => {
    const company = prefillCompany;
    setPrefillCompany(null);
    setActiveModal(null);
    if (company) {
      setLocation(`/companies/${company.id}`);
    }
  };

  const handleContactSubmit = (contactData: any) => {
    const company = prefillCompany;
    createContactMutation.mutate(contactData, {
      onSuccess: () => {
        if (company) {
          setPrefillCompany(null);
          setLocation(`/companies/${company.id}`);
        }
      },
    });
  };

  // Define menu items with context-aware ordering
  const getMenuItems = () => {
    const baseItems = [
      {
        id: "work-order",
        label: "Work Order",
        icon: ClipboardList,
        description: "Create a new work order",
        priority: currentPage === "work-orders" ? 1 : 3,
      },
      {
        id: "company",
        label: "Company",
        icon: Building2,
        description: "Add a new company",
        priority: currentPage === "companies" ? 1 : 4,
      },
      {
        id: "carrier",
        label: "Carrier",
        icon: Truck,
        description: "Add a new carrier",
        priority: currentPage === "carriers" ? 1 : 2,
      },
      {
        id: "contact",
        label: "Contact",
        icon: Users,
        description: "Add a new contact",
        priority: currentPage === "contacts" ? 1 : 5,
      },
    ];

    return baseItems.sort((a, b) => a.priority - b.priority);
  };

  const menuItems = getMenuItems();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="text-sm">
            <Plus className="w-4 h-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">New</span>
            <span className="sm:hidden">New</span>
            <ChevronDown className="w-3 h-3 ml-1 sm:ml-2" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuLabel>Create New</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          {menuItems.map((item) => (
            <DropdownMenuItem
              key={item.id}
              onClick={() => setActiveModal(item.id)}
              className="flex items-center space-x-2 cursor-pointer group"
            >
              <item.icon className="w-4 h-4" />
              <div className="flex flex-col">
                <span className="font-medium">{item.label}</span>
                <span className="text-xs text-muted-foreground group-data-[highlighted]:text-white/90">{item.description}</span>
              </div>
            </DropdownMenuItem>
          ))}

          <DropdownMenuSeparator />
          
          <DropdownMenuItem
            onClick={() => {
              // For notes, we'll handle this differently since notes are contextual
              toast({
                title: "Note Creation",
                description: "Notes can be created from within each entity's detail view",
              });
            }}
            className="flex items-center space-x-2 cursor-pointer text-muted-foreground group"
          >
            <MessageSquare className="w-4 h-4" />
            <div className="flex flex-col">
              <span className="font-medium">Note</span>
              <span className="text-xs text-muted-foreground/70 group-data-[highlighted]:text-white/70">Available in entity details</span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Modal Components */}
      {activeModal === "work-order" && (
        <CreateWorkOrderModal
          onClose={() => setActiveModal(null)}
          onSubmit={handleWorkOrderSubmit}
          isLoading={createWorkOrderMutation.isPending}
          users={users || []}
          currentUserId={currentUser?.id}
        />
      )}

      {activeModal === "company" && (
        <CreateCompanyModal
          onClose={() => setActiveModal(null)}
          onSubmit={handleCompanySubmit}
          isLoading={createCompanyMutation.isPending}
        />
      )}

      {activeModal === "carrier" && (
        <CreateCarrierModal
          onClose={() => setActiveModal(null)}
          onSubmit={handleCarrierSubmit}
          isLoading={createCarrierMutation.isPending}
          companies={companies || []}
        />
      )}

      {activeModal === "contact" && (
        <CreateContactModal
          open={activeModal === "contact"}
          onClose={handleContactModalClose}
          onSubmit={handleContactSubmit}
          isLoading={createContactMutation.isPending}
          defaultCompany={prefillCompany ?? undefined}
        />
      )}

      {showFMCSABulkModal && newlyCreatedCompany && (
        <FMCSACarriersBulkModal
          open={showFMCSABulkModal}
          onClose={() => {
            const companyId = newlyCreatedCompany.id;
            setShowFMCSABulkModal(false);
            setNewlyCreatedCompany(null);
            setLocation(`/companies/${companyId}`);
          }}
          companyId={newlyCreatedCompany.id}
          companyName={newlyCreatedCompany.name}
          dotNumber={newlyCreatedCompany.dotProfile?.dotNumber ?? newlyCreatedCompany.dotNumber ?? null}
          existingCarriers={[]}
          onSuccess={() => {
            const companyId = newlyCreatedCompany.id;
            queryClient.invalidateQueries({ queryKey: ["/api/companies"] });
            queryClient.invalidateQueries({ queryKey: ["/api/carriers"] });
            setShowFMCSABulkModal(false);
            setNewlyCreatedCompany(null);
            toast({ title: "Success", description: "Carriers added from FMCSA successfully" });
            setLocation(`/companies/${companyId}`);
          }}
        />
      )}

      <CommandPalette
        onCreateCompany={() => setActiveModal("company")}
        onCreateCarrier={() => setActiveModal("carrier")}
        onCreateContact={() => setActiveModal("contact")}
        onCreateWorkOrder={() => setActiveModal("work-order")}
      />
    </>
  );
}